<!--
  @component SecurityIO — the root portal for the network/security node.
  chain:   @liquidefy/network-security (control-plane) → io/nodes/security/security-io.ts → this → ctx.security
  tier:    root (mounts INSIDE DeploymentIO so AuditTrailIO can read the stream)
  shape:   control-plane held directly (browser-safe, pure) · the execution
           plane (authoritative authorize+audit) lives in the deployment server.
-->
<script lang="ts" module>
  import { getContext } from "svelte";
  import { SECURITY_IO_KEY } from "./security-io.js";
  import type { SecurityIOContext } from "./security-io.js";

  export { SECURITY_IO_KEY };
  export type { SecurityIOContext };

  export function useSecurityIO(): SecurityIOContext {
    const ctx = getContext<SecurityIOContext | undefined>(SECURITY_IO_KEY);
    if (!ctx) throw new Error("useSecurityIO() called outside of a SecurityIO tree.");
    return ctx;
  }
</script>

<script lang="ts">
  import { setContext, untrack, type Snippet } from "svelte";
  import {
    createSecurityControlNode,
    type RoleName,
    type SecurityControlNode,
    type OpClassification,
  } from "./security-io.js";

  interface Props {
    /** The principal's role for UX pre-checks. Default "viewer" — the
     *  safest default (the server floor is authoritative; under-showing
     *  never grants more than allowed). Refine via `security.whoami`. */
    role?: RoleName;
    children?: Snippet;
  }
  const { role: initialRole = "viewer", children }: Props = $props();

  // Construct ONCE (the deployment precedent — untrack the construction so
  // the rune system does not re-run it on prop change).
  const node: SecurityControlNode = untrack(() => createSecurityControlNode());
  // svelte-ignore state_referenced_locally
  let role = $state<RoleName>(initialRole);

  $effect(() => () => node.destroy());

  const ctx: SecurityIOContext = {
    get ready() { return true; },
    get control() { return node.control; },
    get role() { return role; },
    setRole(next: RoleName) { role = next; },
    can(op: string) { return node.can(role, op); },
    classify(op: string): OpClassification { return node.classify(op); },
  } satisfies SecurityIOContext;

  setContext(SECURITY_IO_KEY, ctx);
</script>

{@render children?.()}
